import React, { useState, useEffect, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Form, Col } from 'react-bootstrap';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import If from '../../../../components/If';
import Loading from '../../../../components/Loading';
import Button from '../../../../components/Button';
import EditButton from '../../../../components/EditButton';
import SearchEmpty from '../../../../components/SearchEmpty';
import api from '../../../../services/api';
import { RecResponsavelTecnicoExtUsoResults } from '../../../../models/models';
import { statusToText } from '../../../../functions/formatStatus';
import '../../styles_list.css';

function normalizeList(data: unknown): RecResponsavelTecnicoExtUsoResults[] {
	if (Array.isArray(data)) {
		return data;
	}
	return [];
}

export default function RecResponsavelTecnicoExtUsoList() {
	const history = useHistory();
	const [items, setItems] = useState<RecResponsavelTecnicoExtUsoResults[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [emptySearch, setEmptySearch] = useState<boolean>(false);

	const { register, handleSubmit, getValues } = useForm({
		defaultValues: {
			descricaoRec: '',
			nomeResponsavel: '',
			emailResponsavel: '',
			active: true
		}
	});

	const loadItems = async (ativo: boolean) => {
		const { data } = await api.get<RecResponsavelTecnicoExtUsoResults[]>(
			`api/v1/cultivares/RecResponsavelTecnicoExtensaoUso/obterporfiltro?Ativo=${ativo}`
		);
		return normalizeList(data);
	};

	useEffect(() => {
		const load = async () => {
			setError(false);
			try {
				const list = await loadItems(getValues('active'));
				setItems(list);
				setEmptySearch(list.length === 0);
			} catch {
				setError(true);
				setItems([]);
				setEmptySearch(false);
			}
			setLoading(false);
		};
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const search = async () => {
		setError(false);
		setLoadingSearch(true);
		setEmptySearch(false);

		try {
			const ativo = getValues('active');
			const { data } = await api.get<RecResponsavelTecnicoExtUsoResults[]>(
				`api/v1/cultivares/RecResponsavelTecnicoExtensaoUso/obterporfiltro` +
					`?DescricaoRec=${encodeURIComponent(getValues('descricaoRec') || '')}` +
					`&NomeResponsavel=${encodeURIComponent(getValues('nomeResponsavel') || '')}` +
					`&EmailResponsavel=${encodeURIComponent(getValues('emailResponsavel') || '')}` +
					`&Ativo=${ativo}`
			);
			const list = normalizeList(data);

			if (list.length) {
				setItems(list);
				setEmptySearch(false);
			} else {
				setItems([]);
				setEmptySearch(true);
			}
		} catch {
			setError(true);
			setItems([]);
		}
		setLoadingSearch(false);
	};

	const actions = ({ data }: any) => (
		<Fragment>
			<EditButton
				onClick={() => {
					history.push({
						pathname: '/editar/responsaveis-rec-extensao-uso',
						state: { id: data.id }
					});
				}}
			/>
		</Fragment>
	);

	return (
		<div className="container-custom-search">
			<div className="top-content">
				<h3>Responsáveis REC (Extensão de Uso)</h3>
			</div>
			<div className="row-buttons">
				<Button
					onClick={() => history.push('/cadastrar/responsaveis-rec-extensao-uso')}
					contained
					title="Adicionar"
					type="button"
				/>
			</div>
			<br />
			<Form onSubmit={handleSubmit(search)}>
				<Form.Row>
					<Form.Group as={Col} md={3}>
						<Form.Label>REC</Form.Label>
						<Form.Control ref={register} name="descricaoRec" autoComplete="off" />
					</Form.Group>
					<Form.Group as={Col} md={3}>
						<Form.Label>Nome do técnico</Form.Label>
						<Form.Control ref={register} name="nomeResponsavel" autoComplete="off" />
					</Form.Group>
					<Form.Group as={Col} md={3}>
						<Form.Label>E-mail</Form.Label>
						<Form.Control ref={register} name="emailResponsavel" autoComplete="off" />
					</Form.Group>
					<Form.Group as={Col} md={2}>
						<Form.Label>Ativo</Form.Label>
						<Form.Control as="select" ref={register} name="active">
							<option value="true">Sim</option>
							<option value="false">Não</option>
						</Form.Control>
					</Form.Group>
				</Form.Row>
				<div className="button-position">
					<Button title="Buscar" type="submit" contained isLoading={loadingSearch} />
				</div>
			</Form>
			<If condition={emptySearch}>
				<div className="div-empty">
					<SearchEmpty />
				</div>
			</If>
			<If condition={!loading && !error && !emptySearch && items.length > 0}>
				<div className="search-content">
					<div className="ag-theme-alpine" style={{ height: '70vh', width: '100%' }}>
						<AgGridReact
							rowData={items}
							defaultColDef={{ flex: 1, sortable: true, resizable: true }}
							pagination
							paginationPageSize={10}
							frameworkComponents={{
								actions: actions
							}}
						>
							<AgGridColumn field="descricaoRec" headerName="REC" wrapText autoHeight />
							<AgGridColumn
								field="nomeResponsavel"
								headerName="Responsável técnico"
								wrapText
								autoHeight
							/>
							<AgGridColumn field="emailResponsavel" headerName="E-mail" />
							<AgGridColumn
								field="ativo"
								headerName="Ativo"
								valueFormatter={({ data: row }: any) => statusToText(row?.ativo)}
								maxWidth={160}
							/>
							<AgGridColumn
								headerName="Ações"
								cellRenderer="actions"
								maxWidth={130}
							/>
						</AgGridReact>
					</div>
				</div>
			</If>
			<If condition={loading}>
				<div className="loading-container">
					<Loading />
				</div>
			</If>
			<If condition={!loading && error}>
				<div className="alert alert-danger mt-3">Erro ao carregar dados. Verifique se a API está no ar e se as migrations foram aplicadas.</div>
			</If>
		</div>
	);
}
